/**
 * One WebRTC connection to one other player.
 *
 * ## Two channels, because a shooter needs two guarantees
 *
 * `state` is unordered and unreliable — `maxRetransmits: 0`. That looks like a
 * downgrade and is the entire point: a position from 80 ms ago is worthless, so
 * retransmitting it costs latency to deliver something already obsolete. Dropping
 * it and waiting 50 ms for the next one is strictly better, and head-of-line
 * blocking on a reliable channel would hold *newer* positions behind the stale one.
 *
 * `event` is ordered and reliable, because "you died" has no next one. A dropped
 * shot is a shot that never happened; a dropped death is two machines that
 * permanently disagree.
 *
 * Getting these the wrong way round is the classic mistake and produces a game
 * that feels fine on a LAN and falls apart on anything else.
 *
 * ## ICE
 *
 * Public STUN only, no TURN. STUN is a free service that tells a browser its own
 * public address so two of them can punch through their routers; TURN relays the
 * traffic when that fails, costs bandwidth, and would have to be paid for. Direct
 * connection succeeds for the large majority of home networks — and for two
 * players on the same wifi, which is the case this was built for. When it fails,
 * it fails visibly rather than silently: see the connection-state handling below.
 */

export const TUNING = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun.cloudflare.com:3478' },
  ],
  /** Give the connection this long to succeed before calling it a failure. */
  connectTimeoutMs: 20_000,
};

export class Peer extends EventTarget {
  /**
   * @param {string} id      the other player's signalling id
   * @param {boolean} polite true for the side that answers rather than offers
   * @param {(msg: object) => void} sendSignal how to reach them via the Worker
   */
  constructor(id, polite, sendSignal) {
    super();
    this.id = id;
    this.polite = polite;
    this.sendSignal = sendSignal;
    this.open = false;
    this.closed = false;

    this.pc = new RTCPeerConnection({ iceServers: TUNING.iceServers });
    this.state = null;
    this.event = null;
    this._pendingIce = [];
    this._remoteSet = false;

    this.pc.addEventListener('icecandidate', (e) => {
      if (e.candidate) this.sendSignal({ t: 'ice', to: this.id, data: e.candidate.toJSON() });
    });

    this.pc.addEventListener('connectionstatechange', () => {
      const s = this.pc.connectionState;
      this.dispatchEvent(new CustomEvent('state', { detail: s }));
      if (s === 'failed' || s === 'closed') this._fail(s);
    });

    this.pc.addEventListener('datachannel', (e) => this._adopt(e.channel));

    this._timeout = setTimeout(() => {
      if (!this.open) this._fail('timeout');
    }, TUNING.connectTimeoutMs);
  }

  /** The offering side creates the channels; the answering side receives them. */
  async offer() {
    this._adopt(
      this.pc.createDataChannel('state', { ordered: false, maxRetransmits: 0, negotiated: false })
    );
    this._adopt(this.pc.createDataChannel('event', { ordered: true }));
    const desc = await this.pc.createOffer();
    await this.pc.setLocalDescription(desc);
    this.sendSignal({ t: 'offer', to: this.id, data: this.pc.localDescription });
  }

  async onOffer(data) {
    await this.pc.setRemoteDescription(new RTCSessionDescription(data));
    this._remoteSet = true;
    await this._flushIce();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    this.sendSignal({ t: 'answer', to: this.id, data: this.pc.localDescription });
  }

  async onAnswer(data) {
    await this.pc.setRemoteDescription(new RTCSessionDescription(data));
    this._remoteSet = true;
    await this._flushIce();
  }

  async onIce(data) {
    /**
     * Candidates routinely arrive before the description they belong to, because
     * the Worker forwards both as fast as it gets them and ICE gathering starts
     * immediately. Adding one early throws; queueing is not an optimisation, it is
     * the difference between connecting and not.
     */
    if (!this._remoteSet) {
      this._pendingIce.push(data);
      return;
    }
    try {
      await this.pc.addIceCandidate(new RTCIceCandidate(data));
    } catch {
      // A rejected candidate is normal — there are several per connection and only
      // one has to work.
    }
  }

  async _flushIce() {
    const queued = this._pendingIce.splice(0);
    for (const c of queued) {
      try {
        await this.pc.addIceCandidate(new RTCIceCandidate(c));
      } catch {
        /* see onIce */
      }
    }
  }

  _adopt(channel) {
    channel.binaryType = 'arraybuffer';
    if (channel.label === 'state') this.state = channel;
    else this.event = channel;

    channel.addEventListener('open', () => {
      // Open when *both* are up: a peer that can hear positions but not deaths is
      // worse than one that is still connecting, because it looks connected.
      if (this.state?.readyState === 'open' && this.event?.readyState === 'open') {
        clearTimeout(this._timeout);
        this.open = true;
        this.dispatchEvent(new CustomEvent('open'));
      }
    });

    channel.addEventListener('message', (e) => {
      this.dispatchEvent(
        new CustomEvent(channel.label === 'state' ? 'state:data' : 'event:data', {
          detail: e.data,
        })
      );
    });

    channel.addEventListener('close', () => {
      if (!this.closed) this._fail('channel closed');
    });
  }

  /** Positions. Dropped silently when the channel is busy — that is the contract. */
  sendState(buf) {
    const ch = this.state;
    if (ch?.readyState !== 'open') return false;
    // A backed-up channel means the network is behind; adding to the queue makes
    // the delay worse rather than better, so a full buffer drops the frame.
    if (ch.bufferedAmount > 64 * 1024) return false;
    try {
      ch.send(buf);
      return true;
    } catch {
      return false;
    }
  }

  /** Facts. Queued by the channel and delivered in order. */
  sendEvent(obj) {
    const ch = this.event;
    if (ch?.readyState !== 'open') return false;
    try {
      ch.send(typeof obj === 'string' ? obj : JSON.stringify(obj));
      return true;
    } catch {
      return false;
    }
  }

  _fail(why) {
    if (this.closed) return;
    this.closed = true;
    this.open = false;
    clearTimeout(this._timeout);
    this.dispatchEvent(new CustomEvent('closed', { detail: { why } }));
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    clearTimeout(this._timeout);
    try {
      this.state?.close();
      this.event?.close();
      this.pc.close();
    } catch {
      /* already gone */
    }
  }
}
